import { useEffect, useRef, useState } from 'react';
import * as api from '../../../../api/apiConnector.js';

/**
 * Custom hook for managing SSE-based lock on collaborative forms
 *
 * Establishes and maintains a Server-Sent Events (SSE) connection to acquire
 * and hold a lock on a collaborative form compilation. Automatically reconnects
 * if the connection is dropped by infrastructure (e.g. HTTP/2 stream reset).
 *
 * @param {string} completionId - ID of the form completion to lock
 * @param {object} user - User object with authentication token
 * @param {boolean} isCollaborative - Whether the form is collaborative
 * @returns {object} Lock state: hasLock, isLocked, lockedBy, connecting, error
 */
const useFormLock = (completionId, user, isCollaborative) => {
  const [state, setState] = useState({
    hasLock: false,
    isLocked: false,
    lockedBy: null,
    connecting: false,
    error: null
  });

  const abortControllerRef = useRef(null);
  const mountedRef = useRef(false);
  const pollIntervalRef = useRef(null);
  const reconnectTimerRef = useRef(null);

  useEffect(() => {
    if (!isCollaborative || !completionId || !user) return;

    mountedRef.current = true;

    const scheduleReconnect = (delayMs = 4000) => {
      if (!mountedRef.current) return;
      reconnectTimerRef.current = setTimeout(() => {
        if (mountedRef.current) connect();
      }, delayMs);
    };

    const connect = async () => {
      if (!mountedRef.current) return;

      // Cancel any pending reconnect timer
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      // Abort any existing connection
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      setState(prev => ({ ...prev, connecting: true, error: null }));
      abortControllerRef.current = new AbortController();

      try {
        const response = await api.connectSSE('darm/formCompletion/lock/connect', {
          user,
          body: { completionId },
          signal: abortControllerRef.current.signal
        });

        // 423 Locked — form already locked by another user
        if (response.status === 423) {
          const data = await response.json();
          if (mountedRef.current) {
            setState({ hasLock: false, isLocked: true, lockedBy: data.lockedBy, connecting: false, error: null });
          }

          // Poll until the lock is released, then reconnect
          pollIntervalRef.current = setInterval(async () => {
            if (!mountedRef.current) {
              clearInterval(pollIntervalRef.current);
              return;
            }
            try {
              const statusResponse = await api.sendMessage('darm/formCompletion/lock/status', {
                user,
                body: { completionId }
              });
              if (statusResponse && !statusResponse.locked) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
                connect();
              }
            } catch (_) {}
          }, 5000);

          return;
        }

        if (!response.ok) {
          throw new Error(`Lock failed: ${response.status}`);
        }

        // Lock acquired
        if (mountedRef.current) {
          setState({ hasLock: true, isLocked: false, lockedBy: null, connecting: false, error: null });
        }

        // Read SSE stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const messages = buffer.split('\n\n');
          buffer = messages.pop() || '';

          for (const msg of messages) {
            if (msg.startsWith('data: ')) {
              try {
                const data = JSON.parse(msg.slice(6));

                if (data.type === 'force_disconnect') {
                  alert(data.message || 'Sei stato disconnesso');
                  window.location.href = '/';
                  return;
                }

                if (data.type === 'actor_completed') {
                  reader.cancel();
                  return;
                }
              } catch (parseError) {
                console.error('Error parsing SSE message:', parseError);
              }
            }
          }
        }

        // Stream ended naturally (server/proxy closed connection) — reconnect
        if (mountedRef.current) {
          scheduleReconnect(2000);
        }

      } catch (error) {
        if (error.name === 'AbortError') return;

        console.error('Lock connection error:', error);

        if (mountedRef.current) {
          setState(prev => ({ ...prev, connecting: false, error: null }));
          // Reconnect after a longer delay on network errors
          scheduleReconnect(5000);
        }
      }
    };

    connect();

    return () => {
      mountedRef.current = false;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [completionId, user?.token, isCollaborative]);

  return state;
};

export default useFormLock;
